import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardMedia, 
  Chip, 
  Collapse,
  CircularProgress
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  PlayCircleOutline as PlayCircleOutlineIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { db } from '../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

interface Video {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  thumbnailUrl: string;
  isIntro: boolean;
  requiredSubscription: boolean;
  module: number;
  category?: string;
  difficulty?: string;
  duration?: number;
  moduleOrder?: number;
  schede?: string;
}

interface VideoProgress {
  [videoId: string]: {
    percentWatched: number;
    completed: boolean;
    hasStarted: boolean;
  };
}

interface CurrentProgress {
  lastPosition: number;
  duration: number;
  percentWatched?: number;
  completed?: boolean;
  hasStarted?: boolean;
}

interface VideoLibraryProps {
  currentVideoId: string;
  onVideoSelect: (videoId: string) => void;
  userProgress: VideoProgress;
  userModules: number[];
  currentVideoModule?: number;
  currentProgress?: CurrentProgress; // Real-time progress for the current video
}

const VideoLibrary: React.FC<VideoLibraryProps> = ({ 
  currentVideoId, 
  onVideoSelect, 
  userProgress, 
  userModules,
  currentVideoModule,
  currentProgress
}) => {
  const { t } = useTranslation();
  const [, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set());
  const [groupedVideos, setGroupedVideos] = useState<{ [key: number]: Video[] }>({});

  // Load videos
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        
        let videoList: Video[] = [];
        
        console.log('VideoLibrary: currentVideoModule =', currentVideoModule);
        
        if (currentVideoModule !== undefined) {
          // If current video has a module, show videos from that module
          console.log('VideoLibrary: Fetching videos for module', currentVideoModule);
          const q = query(
            collection(db, 'videos'),
            where('requiredSubscription', '==', true),
            where('isIntro', '==', false),
            where('module', '==', currentVideoModule)
          );
          const snapshot = await getDocs(q);
          console.log('VideoLibrary: Found', snapshot.docs.length, 'videos for module', currentVideoModule);
          snapshot.forEach((doc) => {
            videoList.push({ id: doc.id, ...doc.data() } as Video);
          });
          
          // Sort by moduleOrder after fetching
          videoList.sort((a, b) => (a.moduleOrder || 0) - (b.moduleOrder || 0));
        } else {
          // If current video has no module, show all videos without modules
          console.log('VideoLibrary: Fetching videos without modules');
          const q = query(
            collection(db, 'videos'),
            where('requiredSubscription', '==', true),
            where('isIntro', '==', false)
          );
          const snapshot = await getDocs(q);
          console.log('VideoLibrary: Found', snapshot.docs.length, 'total videos, filtering for no module');
          snapshot.forEach((doc) => {
            const video = { id: doc.id, ...doc.data() } as Video;
            // Only include videos that don't have a module or have module 0
            if (!video.module || video.module === 0) {
              videoList.push(video);
            }
          });
          
          // Sort by module, then moduleOrder after fetching
          videoList.sort((a, b) => {
            if (a.module !== b.module) return (a.module || 0) - (b.module || 0);
            return (a.moduleOrder || 0) - (b.moduleOrder || 0);
          });
        }
        
        console.log('VideoLibrary: Final videoList length =', videoList.length);
        console.log('VideoLibrary: Videos =', videoList.map(v => ({ id: v.id, title: v.title, module: v.module, moduleOrder: v.moduleOrder })));
        
        setVideos(videoList);
        
        // Group videos by module for display
        const grouped: { [key: number]: Video[] } = {};
        videoList.forEach(video => {
          const moduleKey = video.module || 0; // Use 0 for videos without module
          if (!grouped[moduleKey]) {
            grouped[moduleKey] = [];
          }
          grouped[moduleKey].push(video);
        });
        console.log('VideoLibrary: Grouped videos =', grouped);
        setGroupedVideos(grouped);
        
        // Auto-expand current video's module
        const currentVideo = videoList.find(v => v.id === currentVideoId);
        console.log('VideoLibrary: Current video found =', currentVideo);
        if (currentVideo) {
          const moduleToExpand = currentVideo.module || 0;
          console.log('VideoLibrary: Expanding module =', moduleToExpand);
          setExpandedModules(prev => new Set([...prev, moduleToExpand]));
        } else {
          // If current video not found in filtered list, expand all modules
          console.log('VideoLibrary: Current video not found, expanding all modules');
          const allModules = Object.keys(grouped).map(Number);
          setExpandedModules(new Set(allModules));
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [currentVideoId, userModules, currentVideoModule]);

  const toggleModule = (module: number) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(module)) {
        newSet.delete(module);
      } else {
        newSet.add(module);
      }
      return newSet;
    });
  };

  const getVideoStatus = (videoId: string) => {
    // For current video, use real-time progress if available
    if (videoId === currentVideoId && currentProgress) {
      if (currentProgress.completed) return 'completed';
      if (currentProgress.hasStarted !== false || (currentProgress.lastPosition > 0)) return 'started';
    }
    // Otherwise use saved progress
    const progress = userProgress[videoId];
    if (!progress) return 'not-started';
    if (progress.completed) return 'completed';
    if (progress.hasStarted) return 'started';
    return 'not-started';
  };

  const getStatusIcon = (videoId: string) => {
    const status = getVideoStatus(videoId);
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />;
      case 'started':
        return <PlayCircleOutlineIcon sx={{ color: '#FF9800', fontSize: 20 }} />;
      default:
        return <PlayArrowIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 20 }} />;
    }
  };

  

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 200,
        background: 'rgba(45, 45, 45, 0.85)',
        borderRadius: 2,
        backdropFilter: 'blur(20px)'
      }}>
        <CircularProgress sx={{ color: '#E31C25' }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      background: 'rgba(45, 45, 45, 0.85)',
      backdropFilter: 'blur(20px)',
      borderRadius: 2,
      border: '1px solid rgba(227, 28, 37, 0.2)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Box sx={{
        p: 2,
        background: 'linear-gradient(135deg, rgba(227, 28, 37, 0.1) 0%, transparent 100%)',
        borderBottom: '1px solid rgba(227, 28, 37, 0.2)',
        flexShrink: 0
      }}>
        <Typography variant="h6" sx={{ 
          color: 'white', 
          fontWeight: 700,
          textAlign: 'center',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          {currentVideoModule !== undefined 
            ? `${t('videoPlayer.playlist')} - ${t('dashboard.moduleLabel', { number: currentVideoModule })}`
            : t('videoPlayer.playlist') + ' - ' + t('videoPlayer.noModule')
          }
        </Typography>
      </Box>

      {/* Video List */}
      <Box sx={{ 
        overflowY: 'auto', 
        p: 1,
        maxHeight: { xs: '400px', md: '600px' },
        minHeight: 0 // Allow shrinking
      }}>
        {Object.keys(groupedVideos).length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            py: 3,
            color: 'rgba(255, 255, 255, 0.6)'
          }}>
            <Typography variant="body2">
              {t('videoPlayer.noVideosAvailable')}
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedVideos)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([module, moduleVideos]) => (
              <Box key={module} sx={{ mb: 1 }}>
                {/* Module Header */}
                <Box
                  onClick={() => toggleModule(Number(module))}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    cursor: 'pointer',
                    borderRadius: 1,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(227, 28, 37, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(227, 28, 37, 0.1)',
                      borderColor: 'rgba(227, 28, 37, 0.3)'
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.9rem'
                  }}>
                    {t('dashboard.moduleLabel', { number: Number(module) })}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={moduleVideos.length} 
                      size="small" 
                      sx={{ 
                        background: '#E31C25', 
                        color: 'white',
                        fontSize: '0.7rem',
                        height: 20
                      }} 
                    />
                    <ExpandMoreIcon sx={{ 
                      color: 'white',
                      transform: expandedModules.has(Number(module)) ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }} />
                  </Box>
                </Box>

                {/* Module Videos */}
                <Collapse in={expandedModules.has(Number(module))} timeout="auto" unmountOnExit>
                  <Box sx={{ mt: 1 }}>
                    {moduleVideos.map((video) => (
                      <Card
                        key={video.id}
                        onClick={() => onVideoSelect(video.id)}
                        sx={{
                          mb: 1,
                          cursor: 'pointer',
                          background: video.id === currentVideoId 
                            ? 'rgba(227, 28, 37, 0.2)' 
                            : 'rgba(255, 255, 255, 0.05)',
                          border: video.id === currentVideoId 
                            ? '1px solid #E31C25' 
                            : '1px solid rgba(227, 28, 37, 0.1)',
                          borderRadius: 1.5,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: video.id === currentVideoId 
                              ? 'rgba(227, 28, 37, 0.3)' 
                              : 'rgba(255, 255, 255, 0.08)',
                            transform: 'translateX(4px)',
                            borderColor: '#E31C25'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', gap: 1.5 }}>
                            {/* Thumbnail */}
                            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                              <CardMedia
                                component="img"
                                sx={{
                                  width: 80,
                                  height: 60,
                                  borderRadius: 1,
                                  objectFit: 'cover'
                                }}
                                image={video.thumbnailUrl}
                                alt={video.title}
                              />
                              {/* Status Badge */}
                              <Box sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                background: 'rgba(0, 0, 0, 0.7)',
                                borderRadius: '50%',
                                p: 0.5
                              }}>
                                {getStatusIcon(video.id)}
                              </Box>
                              {/* Duration */}
                              {video.duration && (
                                <Box sx={{
                                  position: 'absolute',
                                  bottom: 4,
                                  right: 4,
                                  background: 'rgba(0, 0, 0, 0.8)',
                                  color: 'white',
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: 0.5,
                                  fontSize: '0.7rem'
                                }}>
                                  {formatDuration(video.duration)}
                                </Box>
                              )}
                            </Box>

                            {/* Video Info */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  lineHeight: 1.3,
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical'
                                }}
                              >
                                {video.title}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                                <Chip
                                  label={video.difficulty 
                                    ? (video.difficulty.charAt(0).toUpperCase() + video.difficulty.slice(1).toLowerCase())
                                    : 'N/A'}
                                  size="small"
                                  sx={{
                                    bgcolor: (() => {
                                      const d = (video.difficulty || '').toLowerCase();
                                      if (d === 'beginner') return 'rgba(76, 175, 80, 0.2)'; // green
                                      if (d === 'intermediate') return 'rgba(255, 193, 7, 0.2)'; // yellow
                                      if (d === 'advanced' || d === 'hard') return 'rgba(244, 67, 54, 0.2)'; // red
                                      return 'rgba(255, 255, 255, 0.2)';
                                    })(),
                                    color: (() => {
                                      const d = (video.difficulty || '').toLowerCase();
                                      if (d === 'beginner') return '#4CAF50';
                                      if (d === 'intermediate') return '#FFC107';
                                      if (d === 'advanced' || d === 'hard') return '#F44336';
                                      return '#FFFFFF';
                                    })(),
                                    border: (() => {
                                      const d = (video.difficulty || '').toLowerCase();
                                      if (d === 'beginner') return '1px solid rgba(76, 175, 80, 0.35)';
                                      if (d === 'intermediate') return '1px solid rgba(255, 193, 7, 0.35)';
                                      if (d === 'advanced' || d === 'hard') return '1px solid rgba(244, 67, 54, 0.35)';
                                      return '1px solid rgba(255, 255, 255, 0.2)';
                                    })(),
                                    fontSize: '0.65rem',
                                    height: 18
                                  }}
                                />
                                {video.moduleOrder && (
                                  <Chip
                                    label={`#${video.moduleOrder}`}
                                    size="small"
                                    sx={{
                                      background: 'rgba(255, 255, 255, 0.2)',
                                      color: 'white',
                                      fontSize: '0.65rem',
                                      height: 18
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Progress Bar - Use real-time progress for current video, otherwise use saved progress */}
                              {(() => {
                                // For current video, use real-time progress if available
                                const isCurrentVideo = video.id === currentVideoId;
                                let displayProgress;
                                
                                if (isCurrentVideo && currentProgress && currentProgress.duration > 0) {
                                  // Calculate percentWatched from real-time progress
                                  const percentWatched = Math.round((currentProgress.lastPosition / currentProgress.duration) * 100);
                                  displayProgress = {
                                    percentWatched: percentWatched,
                                    completed: currentProgress.completed || false,
                                    hasStarted: currentProgress.hasStarted !== false
                                  };
                                } else if (userProgress[video.id]) {
                                  // Use saved progress from Firebase
                                  displayProgress = userProgress[video.id];
                                } else {
                                  return null;
                                }

                                return (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Box sx={{
                                      width: '100%',
                                      height: 3,
                                      background: 'rgba(255, 255, 255, 0.2)',
                                      borderRadius: 1.5,
                                      overflow: 'hidden'
                                    }}>
                                      <Box sx={{
                                        width: `${displayProgress.percentWatched}%`,
                                        height: '100%',
                                        background: 'linear-gradient(90deg, #E31C25, #ff4757)',
                                        borderRadius: 1.5,
                                        transition: 'width 0.3s ease'
                                      }} />
                                    </Box>
                                    <Typography variant="caption" sx={{ 
                                      color: 'rgba(255, 255, 255, 0.6)',
                                      fontSize: '0.65rem'
                                    }}>
                                      {displayProgress.percentWatched}% {t('videoPlayer.watched')}
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))
        )}
      </Box>
    </Box>
  );
};

export default VideoLibrary;

